import React, { useEffect, useRef, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  CameraIcon,
  ConfirmDialog,
  IconButton,
  PrimaryButton,
  SecondaryButton,
  TrashIcon,
  XIcon,
} from "../../design-system";
import { borders, surfaces, textColors, typography } from "../../design-system/tokens";
import {
  deleteTemplatePreview,
  deleteTemplatePreviews,
  deleteTemplatePreviewScreenshots,
  fetchTemplatePreviews,
  rebuildTemplatePreviews,
  screenshotTemplatePreviews,
} from "../utils/adminApi";

const mergePreviewsByDomain = (current = [], incoming = []) => {
  const map = new Map();
  (current || []).forEach((p) => {
    if (p && p.domain) map.set(p.domain, p);
  });
  (incoming || []).forEach((p) => {
    if (!p || !p.domain) return;
    const existing = map.get(p.domain) || {};
    map.set(p.domain, { ...existing, ...p });
  });
  return Array.from(map.values()).sort((a, b) => String(a.domain || "").localeCompare(String(b.domain || "")));
};

const WebsitePreviewsAdminPage = () => {
  const [previews, setPreviews] = useState([]);
  const [expectedCount, setExpectedCount] = useState(0);
  const [existingCount, setExistingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [buildingMissing, setBuildingMissing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletingIds, setDeletingIds] = useState(new Set());
  const [screenshotting, setScreenshotting] = useState(false);
  const [screenshottingKeys, setScreenshottingKeys] = useState(new Set());
  const [confirmDeleteScreenshotsOpen, setConfirmDeleteScreenshotsOpen] = useState(false);
  const [deletingScreenshots, setDeletingScreenshots] = useState(false);
  const [deletingScreenshotKeys, setDeletingScreenshotKeys] = useState(new Set());
  const isMountedRef = useRef(true);

  const loadPreviews = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchTemplatePreviews();
      setPreviews(data?.results || []);
      setExpectedCount(data?.expected_count ?? 0);
      setExistingCount(data?.existing_count ?? (data?.results || []).length);
    } catch (err) {
      setError(err?.message || "Failed to load previews");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPreviews();
  }, []);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);


  const handleRebuild = async () => {
    setRebuilding(true);
    setNotice(null);
    setConfirmOpen(false);
    try {
      const batchSize = 5;
      let reset = true;
      let createdTotal = 0;

      // Since reset deletes all previews, clear UI immediately to avoid showing stale rows mid-rebuild.
      setPreviews([]);
      setExistingCount(0);

      // Keep looping until backend reports we're done.
      // Backend decides which previews to create next; client only requests "the next batch".
      for (let i = 0; i < 1000; i += 1) {
        const result = await rebuildTemplatePreviews({ reset, batch_size: batchSize });
        reset = false;

        const created = result?.created || [];
        createdTotal += created.length;

        if (!isMountedRef.current) return;

        setExpectedCount(result?.expected_count ?? 0);
        setExistingCount(result?.existing_count ?? 0);
        if (created.length) {
          setPreviews((prev) => mergePreviewsByDomain(prev, created));
        }

        const expected = result?.expected_count ?? 0;
        const existing = result?.existing_count ?? 0;
        const remaining = result?.remaining_count ?? Math.max(0, expected - existing);
        const done = !!result?.done;

        if (!done) {
          setNotice({
            type: "success",
            message: `Rebuilding previews… ${existing}/${expected} created (${remaining} remaining).`,
          });
        }

        if (done) break;
        if (!created.length) {
          throw new Error("Rebuild made no progress. Please refresh and try again.");
        }
      }

      setNotice({
        type: "success",
        message: `Rebuilt previews. Created ${createdTotal} preview(s).`,
      });
      await loadPreviews();
    } catch (err) {
      setNotice({
        type: "error",
        message: err?.message || "Failed to rebuild previews",
      });
    } finally {
      setRebuilding(false);
    }
  };

  const handleBuildMissing = async () => {
    setBuildingMissing(true);
    setNotice(null);
    try {
      const batchSize = 5;
      let createdTotal = 0;

      for (let i = 0; i < 1000; i += 1) {
        const result = await rebuildTemplatePreviews({ reset: false, batch_size: batchSize });

        const created = result?.created || [];
        createdTotal += created.length;

        if (!isMountedRef.current) return;

        setExpectedCount(result?.expected_count ?? 0);
        setExistingCount(result?.existing_count ?? 0);
        if (created.length) {
          setPreviews((prev) => mergePreviewsByDomain(prev, created));
        }

        const expected = result?.expected_count ?? 0;
        const existing = result?.existing_count ?? 0;
        const remaining = result?.remaining_count ?? Math.max(0, expected - existing);
        const done = !!result?.done;

        if (!done) {
          setNotice({
            type: "success",
            message: `Building missing previews… ${existing}/${expected} done (${remaining} remaining).`,
          });
        }

        if (done) break;
        if (!created.length) break;
      }

      if (createdTotal === 0) {
        setNotice({ type: "success", message: "All previews are already built. Nothing to do." });
      } else {
        setNotice({
          type: "success",
          message: `Built ${createdTotal} missing preview(s). All previews are now up to date.`,
        });
      }
      await loadPreviews();
    } catch (err) {
      setNotice({
        type: "error",
        message: err?.message || "Failed to build missing previews",
      });
    } finally {
      setBuildingMissing(false);
    }
  };

  const handleDeleteSingle = async (containerId) => {
    if (!containerId) return;
    setDeletingIds((prev) => new Set(prev).add(containerId));
    try {
      await deleteTemplatePreview(containerId);
      if (!isMountedRef.current) return;
      setPreviews((prev) => prev.filter((p) => p.container_id !== containerId));
      setExistingCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      if (!isMountedRef.current) return;
      setNotice({ type: "error", message: err?.message || "Failed to delete preview" });
    } finally {
      if (isMountedRef.current) {
        setDeletingIds((prev) => {
          const next = new Set(prev);
          next.delete(containerId);
          return next;
        });
      }
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setNotice(null);
    try {
      const result = await deleteTemplatePreviews();
      const deletedCount = result?.deleted_ids?.length ?? 0;
      setNotice({
        type: "success",
        message: `Deleted ${deletedCount} preview container(s).`,
      });
      await loadPreviews();
    } catch (err) {
      setNotice({
        type: "error",
        message: err?.message || "Failed to delete previews",
      });
    } finally {
      setDeleting(false);
      setConfirmDeleteOpen(false);
    }
  };

  const handleScreenshotAll = async () => {
    setScreenshotting(true);
    setNotice({ type: "success", message: "Screenshotting all template previews… this may take several minutes." });
    try {
      const data = await screenshotTemplatePreviews();
      if (!isMountedRef.current) return;
      setNotice({ type: "success", message: data?.message || "All screenshots generated." });
      await loadPreviews();
    } catch (err) {
      if (!isMountedRef.current) return;
      setNotice({ type: "error", message: err?.message || "Failed to screenshot template previews" });
    } finally {
      if (isMountedRef.current) setScreenshotting(false);
    }
  };


  const handleScreenshotSingle = async (domain) => {
    if (!domain) return;
    setScreenshottingKeys((prev) => new Set(prev).add(domain));
    setNotice({ type: "success", message: `Screenshotting "${domain}"…` });
    try {
      const data = await screenshotTemplatePreviews(domain);
      if (!isMountedRef.current) return;
      setNotice({ type: "success", message: data?.message || `Screenshot generated for "${domain}".` });
      await loadPreviews();
    } catch (err) {
      if (!isMountedRef.current) return;
      setNotice({ type: "error", message: err?.message || `Failed to screenshot "${domain}"` });
    } finally {
      if (isMountedRef.current) {
        setScreenshottingKeys((prev) => {
          const next = new Set(prev);
          next.delete(domain);
          return next;
        });
      }
    }
  };

  const handleDeleteAllScreenshots = async () => {
    setDeletingScreenshots(true);
    setNotice(null);
    setConfirmDeleteScreenshotsOpen(false);
    try {
      const data = await deleteTemplatePreviewScreenshots();
      if (!isMountedRef.current) return;
      setNotice({ type: "success", message: data?.message || "All screenshots deleted." });
      await loadPreviews();
    } catch (err) {
      if (!isMountedRef.current) return;
      setNotice({ type: "error", message: err?.message || "Failed to delete screenshots" });
    } finally {
      if (isMountedRef.current) setDeletingScreenshots(false);
    }
  };

  const handleDeleteSingleScreenshot = async (domain) => {
    if (!domain) return;
    setDeletingScreenshotKeys((prev) => new Set(prev).add(domain));
    try {
      const data = await deleteTemplatePreviewScreenshots(domain);
      if (!isMountedRef.current) return;
      setNotice({ type: "success", message: data?.message || `Screenshot deleted for "${domain}".` });
      await loadPreviews();
    } catch (err) {
      if (!isMountedRef.current) return;
      setNotice({ type: "error", message: err?.message || `Failed to delete screenshot for "${domain}"` });
    } finally {
      if (isMountedRef.current) {
        setDeletingScreenshotKeys((prev) => {
          const next = new Set(prev);
          next.delete(domain);
          return next;
        });
      }
    }
  };

  return (
    <div className={`min-h-screen ${surfaces.modernApp}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className={`${typography.h2} ${textColors.primary}`}>Website Previews</h1>
            <p className={`${typography.bodySmall} ${textColors.secondary}`}>
              Manage persisted template previews for CreateNewWebsite.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
            <SecondaryButton onClick={loadPreviews} disabled={loading} className="w-full sm:w-auto whitespace-nowrap">
              {loading ? "Refreshing..." : "Refresh"}
            </SecondaryButton>
            <PrimaryButton onClick={handleBuildMissing} disabled={buildingMissing || rebuilding} className="w-full sm:w-auto whitespace-nowrap">
              {buildingMissing ? "Building Missing..." : "Build Missing"}
            </PrimaryButton>
            <SecondaryButton onClick={() => setConfirmOpen(true)} disabled={rebuilding || buildingMissing} className="w-full sm:w-auto whitespace-nowrap">
              {rebuilding ? "Rebuilding..." : "Rebuild All"}
            </SecondaryButton>
            <SecondaryButton
              leftIcon={<CameraIcon size="sm" />}
              onClick={handleScreenshotAll}
              disabled={screenshotting}
              className="w-full sm:w-auto whitespace-nowrap"
              title="Generate Playwright screenshots for all template previews (local dev only)"
            >
              {screenshotting ? "Screenshotting..." : "Screenshot All"}
            </SecondaryButton>
            <SecondaryButton
              leftIcon={<TrashIcon size="sm" />}
              onClick={() => setConfirmDeleteOpen(true)}
              disabled={deleting}
              className="w-full sm:w-auto whitespace-nowrap"
            >
              {deleting ? "Deleting..." : "Delete Previews"}
            </SecondaryButton>
            <SecondaryButton
              leftIcon={<TrashIcon size="sm" />}
              onClick={() => setConfirmDeleteScreenshotsOpen(true)}
              disabled={deletingScreenshots}
              className="w-full sm:w-auto whitespace-nowrap"
              title="Delete all template preview screenshot PNG files"
            >
              {deletingScreenshots ? "Deleting..." : "Delete Screenshots"}
            </SecondaryButton>
          </div>
        </div>

        {notice && (
          <div
            className={`mb-4 rounded-2xl border px-4 py-3 ${notice.type === "error" ? "border-red-300 text-red-700 bg-red-50" : "border-green-300 text-green-700 bg-green-50"}`}
          >
            {notice.message}
          </div>
        )}

        {error && (
          <div className="mb-4 rounded-2xl border border-red-300 bg-red-50 text-red-700 px-4 py-3">
            {error}
          </div>
        )}

        <Card variant="glass">
          <CardHeader
            title="Current Preview Websites"
            subtitle={`Existing: ${existingCount} • Expected: ${expectedCount}`}
          />
          <CardBody>
            {loading ? (
              <div className={`${typography.body} ${textColors.secondary}`}>Loading previews…</div>
            ) : previews.length === 0 ? (
              <div className={`${typography.body} ${textColors.secondary}`}>No previews found.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className={`w-full text-left ${typography.bodySmall}`}>
                  <thead>
                    <tr className={`border-b ${borders.glass}`}>
                      <th className="py-2 px-2 sm:px-4">Domain</th>
                      <th className="py-2 px-2 sm:px-4 hidden sm:table-cell">Template</th>
                      <th className="py-2 px-2 sm:px-4">Style</th>
                      <th className="py-2 px-2 sm:px-4 hidden sm:table-cell">Website ID</th>
                      <th className="py-2 px-2 sm:px-4 hidden sm:table-cell">Version Slug</th>
                      <th className="py-2 px-2 sm:px-4 hidden sm:table-cell">Published</th>
                      <th className="py-2 px-2 sm:px-4 hidden sm:table-cell">Screenshot</th>
                      <th className="py-2 px-2 sm:px-4 hidden sm:table-cell">Expected</th>
                      <th className="py-2 px-2 w-20" />
                    </tr>
                  </thead>
                  <tbody>
                    {previews.map((preview) => {
                      const cid = preview.container_id;
                      const dom = preview.domain;
                      const isRowDeleting = cid && deletingIds.has(cid);
                      const isRowScreenshotting = dom && screenshottingKeys.has(dom);
                      const isRowDeletingScreenshot = dom && deletingScreenshotKeys.has(dom);
                      return (
                        <tr
                          key={cid ?? dom}
                          className={`border-b ${borders.glass} ${isRowDeleting ? "opacity-40" : ""}`}
                        >
                          <td className="py-2 px-2 sm:px-4 min-w-0 truncate">{dom}</td>
                          <td className="py-2 px-2 sm:px-4 hidden sm:table-cell">{(preview.template || "—").replace("-navigation", "")}</td>
                          <td className="py-2 px-2 sm:px-4">{preview.style || "—"}</td>
                          <td className="py-2 px-2 sm:px-4 hidden sm:table-cell">{preview.website_id ?? "—"}</td>
                          <td className="py-2 px-2 sm:px-4 hidden sm:table-cell">{preview.version_slug || "—"}</td>
                          <td className="py-2 px-2 sm:px-4 hidden sm:table-cell">{preview.is_published ? "Yes" : "No"}</td>
                          <td className="py-2 px-2 sm:px-4 hidden sm:table-cell">
                            {preview.has_screenshot ? (
                              <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">Yes</span>
                            ) : (
                              <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">No</span>
                            )}
                          </td>
                          <td className="py-2 px-2 sm:px-4 hidden sm:table-cell">{preview.expected ? "Yes" : "No"}</td>
                          <td className="py-2 px-2">
                            <div className="flex items-center justify-end gap-3">
                              <IconButton
                                icon={<CameraIcon size="sm" />}
                                disabled={!dom || isRowScreenshotting || screenshotting}
                                onClick={() => handleScreenshotSingle(dom)}
                                variant="secondary"
                                size="sm"
                                shape="square"
                                aria-label={`Screenshot ${dom}`}
                                title={isRowScreenshotting ? "Screenshotting…" : `Screenshot ${dom}`}
                              />
                              <IconButton
                                icon={
                                  <span className="relative inline-flex">
                                    <CameraIcon size="sm" />
                                    <XIcon size="xs" className="absolute -top-1 -right-1.5" />
                                  </span>
                                }
                                onClick={() => handleDeleteSingleScreenshot(dom)}
                                disabled={!dom || isRowDeletingScreenshot}
                                variant="secondary"
                                size="sm"
                                shape="square"
                                aria-label={`Delete screenshot ${dom}`}
                                title={`Delete screenshot for ${dom}`}
                              />
                              <IconButton
                                icon={<XIcon size="sm" />}
                                onClick={() => handleDeleteSingle(cid)}
                                disabled={!cid || isRowDeleting}
                                variant="secondary"
                                size="sm"
                                shape="square"
                                aria-label={`Delete preview ${dom}`}
                                title={`Delete preview container ${dom}`}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleRebuild}
        title="Rebuild all template previews?"
        message="This will delete all existing preview websites and recreate them for every template/style combination."
        confirmText="Rebuild"
        cancelText="Cancel"
        variant="danger"
        isLoading={rebuilding}
      />
      <ConfirmDialog
        isOpen={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDelete}
        title="Delete all template previews?"
        message="This will delete all preview websites. You can recreate them later with the rebuild action."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={deleting}
      />
      <ConfirmDialog
        isOpen={confirmDeleteScreenshotsOpen}
        onClose={() => setConfirmDeleteScreenshotsOpen(false)}
        onConfirm={handleDeleteAllScreenshots}
        title="Delete all template preview screenshots?"
        message="This will delete all screenshot PNG files from public/template-previews/. The preview websites themselves are not affected."
        confirmText="Delete Screenshots"
        cancelText="Cancel"
        variant="danger"
        isLoading={deletingScreenshots}
      />
    </div>
  );
};

export default WebsitePreviewsAdminPage;
